import { useCallback } from "react";
import * as ImagePicker from "expo-image-picker";
import { useApiClient } from "@src/packages/api/louise-api";
import { throwApiError } from "@src/packages/api/api-error";
import type { ResponseBody } from "@src/packages/openapi/helpers";
import type { paths } from "@src/packages/openapi/schema";
import { preparePackPhoto } from "./photoCapture";
import { withMedicationLookupDeadline } from "./request-timeout";
import {
  MedicationPackPhotoError,
  describeError,
} from "./useMedicationCatalog";

export type MedicationCodeResolution = ResponseBody<
  paths["/medication-catalog/resolve-code"]["get"]
>["data"];
type MedicationCatalogDetails = ResponseBody<
  paths["/medication-catalog/{medicationId}"]["get"]
>["data"];

export type MedicationPackScanResult = {
  resolution: MedicationCodeResolution;
  scannedCode: string;
  catalog: MedicationCatalogDetails | null;
};

/**
 * What the patient can do about a decode that failed.
 *
 * Each status is a different thing for her to try, so they are told apart
 * rather than collapsed into one "could not read". 422 covers every "nothing
 * usable here" case — no barcode, a reader that gave up, a label that is not
 * a pack code — so its copy asks for the box's own barcode. 415 is a format
 * or an image whose pixels will not decode; 413 is the upload ceiling, which
 * `preparePackPhoto`'s re-encode keeps us well under. A 400 or 403 is ours to
 * fix, not hers to act on, and falls through to the generic failure.
 */
function packDecodeFailure(status: number, detail: string) {
  if (status === 422) {
    return new MedicationPackPhotoError("unreadable", detail, "request");
  }
  if (status === 413) {
    return new MedicationPackPhotoError("too_large", detail, "request");
  }
  if (status === 415) {
    return new MedicationPackPhotoError("unsupported", detail, "request");
  }
  return null;
}

/**
 * A request that never reached an answer — offline, DNS, a dropped
 * connection, or the deadline above — reads as unreachable rather than as an
 * unreadable photo, because retrying the photo is not what fixes it.
 */
function packDecodeUnreachable(error: unknown) {
  return new MedicationPackPhotoError(
    "unreachable",
    describeError(error),
    "request",
  );
}

/** Code normalization and identification belong to the catalog, not the client. */
export function useMedicationPackScan() {
  const { client, patientId } = useApiClient();
  const enrichResolution = useCallback(
    async (
      resolution: MedicationCodeResolution,
      scannedCode: string,
    ): Promise<MedicationPackScanResult> => {
      let catalog: MedicationCatalogDetails | null = null;
      const medicationId = resolution.medicationId;
      if (resolution.resolution === "exact" && medicationId) {
        // A barcode identifies a pack; its digits do not encode an administration route.
        // Missing metadata must leave the route editable, not discard a valid scan.
        try {
          const response = await withMedicationLookupDeadline(
            (signal) =>
              client.GET("/medication-catalog/{medicationId}", {
                signal,
                params: {
                  path: { medicationId },
                  query: patientId ? { patientId } : {},
                },
              }),
            5_000,
          );
          catalog = response.data?.data ?? null;
        } catch {
          catalog = null;
        }
      }
      return { resolution, scannedCode, catalog };
    },
    [client, patientId],
  );

  const resolveCode = useCallback(
    async (code: string, countryCode: string | null) => {
      let response;
      try {
        response = await withMedicationLookupDeadline(
          (signal) =>
            client.GET("/medication-catalog/resolve-code", {
              signal,
              params: {
                query: {
                  code,
                  ...(countryCode ? { countryCode } : {}),
                  ...(patientId ? { patientId } : {}),
                },
              },
            }),
          20_000,
        );
      } catch (error) {
        throw packDecodeUnreachable(error);
      }
      if (response.error) {
        const status = response.response.status;
        const failure = packDecodeFailure(
          status,
          `HTTP ${status} ${JSON.stringify(response.error).slice(0, 300)}`,
        );
        if (failure) throw failure;
        throwApiError(
          response.error,
          "Failed to read the pack code",
          "medication-catalog",
          status,
        );
      }
      if (!response.data?.data) {
        throw new MedicationPackPhotoError(
          "failed",
          "Empty pack code resolution",
          "request",
        );
      }
      return enrichResolution(response.data.data, code);
    },
    [client, enrichResolution, patientId],
  );

  const resolvePhoto = useCallback(
    async (uri: string, fileNameHint: string, countryCode: string | null) => {
      const { blob, fileName } = await preparePackPhoto(uri, fileNameHint);
      let response;
      try {
        response = await withMedicationLookupDeadline(
          (signal) =>
            client.POST("/medication-catalog/resolve-code", {
              signal,
              params: { query: patientId ? { patientId } : {} },
              body: {
                image: fileName,
                ...(countryCode ? { countryCode } : {}),
              },
              bodySerializer: (body) => {
                const form = new FormData();
                form.append("image", blob, fileName);
                if (body?.countryCode) {
                  form.append("countryCode", body.countryCode);
                }
                return form;
              },
            }),
          30_000,
        );
      } catch (error) {
        throw packDecodeUnreachable(error);
      }
      if (response.error) {
        const status = response.response.status;
        const failure = packDecodeFailure(
          status,
          `HTTP ${status} ${JSON.stringify(response.error).slice(0, 300)}`,
        );
        if (failure) throw failure;
        throwApiError(
          response.error,
          "Failed to read the pack photo",
          "medication-catalog",
          status,
        );
      }
      if (!response.data?.data) {
        throw new MedicationPackPhotoError(
          "failed",
          "Empty pack photo resolution",
          "request",
        );
      }
      return enrichResolution(
        response.data.data,
        response.data.data.normalizedCode,
      );
    },
    [client, enrichResolution, patientId],
  );

  const scanPhoto = useCallback(
    async (countryCode: string | null) => {
      const picked = await ImagePicker.launchCameraAsync({
        mediaTypes: ["images"],
        cameraType: ImagePicker.CameraType.back,
        quality: 0.7,
      });
      if (picked.canceled || !picked.assets?.length) return null;
      const asset = picked.assets[0]!;
      return resolvePhoto(asset.uri, asset.fileName || "pack.jpg", countryCode);
    },
    [resolvePhoto],
  );

  return { resolveCode, resolvePhoto, scanPhoto };
}
